import React, { useState, useEffect } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import dashboardAPI from '../../../services/dashboardService';
import { Share2, Heart, MessageCircle } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
    </div>
);

const DetailedSocialPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;
    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    const fetchStats = async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getSocialStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error("Failed to fetch social stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats(filters);
    }, [filters]);

    if (loading || !stats) {
        return <div className="p-8 text-center text-gray-500">Loading statistics...</div>;
    }

    const postTypeData = stats.postsByType ? Object.entries(stats.postsByType).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: value
    })) : [];

    const graphData = stats.graph_data || [];

    return (
        <DetailedPageLayout
            title="Social Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            {/* Top Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                <StatCard
                    title="Total Posts"
                    value={stats.totalPosts}
                    icon={Share2}
                    color="bg-purple-500"
                />
                <StatCard
                    title="Total Likes"
                    value={stats.likes}
                    icon={Heart}
                    color="bg-pink-500"
                />
                <StatCard
                    title="Total Comments"
                    value={stats.comments}
                    icon={MessageCircle}
                    color="bg-blue-500"
                />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                    <CustomDonutChart
                        data={postTypeData}
                        totalValue={stats.totalPosts}
                        title="Content Distribution"
                    />
                </div>
                <div className="lg:col-span-2">
                    <CustomAreaChart
                        data={graphData}
                        color="#8b5cf6"
                        title="Engagement Trends"
                    />
                </div>
            </div>

            {/* Top Posts Table */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-gray-900 font-semibold mb-4">Top Performing Posts</h3>
                <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                    <table className="w-full text-left min-w-[500px] md:min-w-full">
                        <thead>
                            <tr className="border-b border-gray-100 text-gray-500 text-xs md:text-sm">
                                <th className="pb-3 font-medium">Content</th>
                                <th className="pb-3 font-medium">Author</th>
                                <th className="pb-3 font-medium">Type</th>
                                <th className="pb-3 font-medium text-right">Engagement</th>
                            </tr>
                        </thead>
                        <tbody className="text-xs md:text-sm">
                            {stats.topPosts && stats.topPosts.map(post => (
                                <tr key={post.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                                    <td className="py-3 pr-4 max-w-[150px] md:max-w-[300px] truncate">{post.content}</td>
                                    <td className="py-3 text-gray-600 truncate max-w-[100px]">{post.author}</td>
                                    <td className="py-3 text-gray-500">
                                        <span className="bg-gray-100 px-2 py-0.5 rounded text-[10px] md:text-xs">
                                            {post.type}
                                        </span>
                                    </td>
                                    <td className="py-3 text-right font-semibold text-gray-900">{post.engagementCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedSocialPage;
